import React, { useEffect, useMemo, useRef, useState } from 'react'
import styles from "../css/TagMenu.module.css"
import useCoords from '../../../hooks/useCoords'
import { FaTags } from 'react-icons/fa'
import { LuPlus } from "react-icons/lu";
import { useDispatch, useSelector } from 'react-redux'
import { getTags } from '../../../actions/tagActions'
import { TAG_RESET } from '../../../actions/tagTypes'
import { useCallback } from 'react'
import { FiSearch } from 'react-icons/fi'

const TagMenu = ({
    selectedTags,
    addTag,
    removeTag,
    closeMenu,
    modal
}) => {
    const tags = useSelector((state) => state.tagState.tags)
    const crashError = useSelector((state) => state.tagState.fetchError)
    const dispatch = useDispatch()
    const fetching = useRef(true)

    useEffect(()=>{
        if (fetching.current){
            fetching.current = false
            dispatch(getTags())
        }
        return () => {
            dispatch({type: TAG_RESET})
        }
    },[dispatch])


    useEffect(() => {
        if (crashError){
            closeMenu()
        }
    }, [closeMenu,crashError])

    return(
        tags ? 
        <TagDropDown
            tags = {tags}
            selectedTags = {selectedTags}
            addTag = {addTag}
            removeTag = {removeTag}
            closeMenu = {closeMenu}
            modal = {modal}
        />
        : <LoadingMenu modal = {modal} closeMenu = {closeMenu} />
    )
}

const TagDropDown = ({
    tags,
    selectedTags,
    addTag,
    removeTag,
    closeMenu,
    modal
}) => {
    const [search,setSearch] = useState("")
    const [error,setError] = useState("")
    const tagsMenu = useRef(null)
    useCoords(tagsMenu,modal,selectedTags)

    useEffect(() => {
        const handleClick = (e) => {
            let btn = modal === "tag-filter-menu" ? document.getElementById("tag-filter-btn") : tagsMenu.current?.previousElementSibling
            if (!tagsMenu.current?.contains(e.target) && !btn?.contains(e.target)){
                    closeMenu()
            }
        }
        window.addEventListener("mousedown",handleClick)
        return () => {
            window.removeEventListener("mousedown",handleClick)
        };
    }, [closeMenu])

    const isSelected = useCallback((tag)=>{
        return selectedTags.map(item => item._id).includes(tag._id)
    },[selectedTags])

    const tagResults = useMemo(() => {
        let query = search.toLowerCase()
        return tags ? tags.filter(tag => tag.name.toLowerCase().includes(query)) : null
    },[tags,search])

    const selectTag = (tag) => {
        if (isSelected(tag)){
            removeTag(tag._id)
        }
        else{
            addTag(tag)
        }
    }

    const createTag = () => {
        let value = search.replace(/\s\s+/g, ' ').trim()
        if (value){
            if (value.length < 2){
                setError("Minimum length is 2")
            }
            else if (value.length > 18){
                setError("Maximum length is 18")
            }
            else{
                addTag({name:value})
                setSearch("")
                closeMenu()
            }
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === "Enter"){
            e.preventDefault()
            createTag()
        }
    }

    const isUniqueTag = useMemo(()=>{
        let value = search.replace(/\s\s+/g, ' ').trim().toLowerCase()
        return !tags ? false : !tags.map(tag => tag.name.toLowerCase()).includes(value) && !selectedTags.map(tag => tag.name.toLowerCase()).includes(value)
    },[tags,search,selectedTags])

    const handleSearch = (e) => {
        let value = e.target.value
        if (value.length <= 18 && error === "Maximum length is 18"){
            setError("")
        }
        if (value.length >= 2 && error === "Minimum length is 2"){
            setError("")
        }
        setSearch(value)
    }


    return (
        <div id = "tag-filter-menu" data-testid = "tag-menu" className = {`${styles.tagDropdown} ${modal === "tag-filter-menu" ? styles.filter : ""}`}  ref = {tagsMenu}>
            <div className={styles.tagSearch}>
                <span className={styles.tagSearchIcon}><FiSearch/></span>
                <div className = {styles.inputContainer}>
                    <input
                        type = "text" 
                        name = "search" 
                        value = {search} 
                        onChange = {handleSearch} 
                        onKeyDown = {handleKeyDown} 
                        placeholder = {modal === "tag-filter-menu" ? "Search tags..." : "Search or Create New"} 
                        autoComplete='off'
                    />
                </div>
            </div>
            {error && <div className = {styles.errorBox}>{error}</div>}
            {(modal !== "tag-filter-menu" && search.trim() && isUniqueTag) && <div className = {`${styles.tagItem} ${styles.addBtn}`} onClick = {()=>createTag()}>
                <div className = {styles.iconWrapper}>
                    <div className = {styles.tagIcon}><FaTags/></div>
                    <div className = {styles.plusIcon}><LuPlus/></div>
                </div>
                <div className = {styles.addText}>Create <b>&ldquo;{search}&rdquo;</b></div>
            </div>}
            {tagResults.length ? <div className={styles.tagList}>
                    {tagResults.map((tag)=>(
                        <div className={`${styles.tagItem} ${isSelected(tag) ? styles.active : ""}`} key = {tag._id}>
                            <div data-testid = {`tag-${tag._id}`} className={styles.tagCheckbox} onClick = {()=>selectTag(tag)}>
                                {isSelected(tag) && <span className={styles.tagCheckboxTick}>✓</span>}
                            </div>
                            <span className={styles.tagItemName}>{tag.name}</span>
                        </div>
                    ))}
                </div>
                : 
                (modal === "tag-filter-menu" && <div className = {styles.emptyArea}>
                    <div className = {styles.icon}><FaTags/></div>
                    <div className = {styles.emptyText}>No Tags Found</div>
                </div>)}
            <div className={styles.tagHint}>{selectedTags.length} of 5 selected</div>
        </div> 
    )
}

const LoadingMenu = ({modal,closeMenu}) => {
    const loadingMenu = useRef(null)
    useCoords(loadingMenu,modal)

    useEffect(() => {
        const handleClick = (e) => {
            if (!loadingMenu.current?.parentNode?.contains(e.target)){
                    closeMenu()
            }
          }
        window.addEventListener("mousedown",handleClick)
        return () => {
            window.removeEventListener("mousedown",handleClick)
        };
    }, [closeMenu])

    return (
        <div id = "tag-filter-menu" className = {`${styles.tagDropdown} ${modal === "tag-filter-menu" ? styles.filter : ""}`} ref = {loadingMenu}>
            <div className = {styles.loadingDots}>
                {[...Array(3).keys()].map((_,index)=>(
                    <div className = {styles.dot} key = {index}></div>
                ))}
            </div>
        </div>
    )
}

export default TagMenu